import { useState } from 'react';
import { Text, TextInput, View } from 'react-native';
import { Badge, Icon } from '../index';
import { useTranslation } from 'react-i18next';

import type { DraftSet, WeightUnit } from './state/loggerDraft';
import type { useTheme } from './theme';
import {
  isLoadBearingKind,
  numericFieldErrorText,
  type IntensityKind,
} from '@jackedlogs/shared';
import { steppedWeight } from './weightStepper';
import { useFieldFocusReporter } from './useFieldFocus';
import { useSelectAllOnFocus } from './useSelectAllOnFocus';
import { SetRowStepperBar } from './SetRowStepperBar';
import { FocusPanelCardio } from './FocusPanelCardio';
import { FocusPanelTimed } from './FocusPanelTimed';
import { HeaderTooltip } from './HeaderTooltip';
import { makeFocusPanelInputsStyles } from './focusPanelInputsStyles';
import { amrapNeedsReps } from './amrapGate';
import { STEPS_KG, STEPS_LB, STEPS_PERCENT } from './weightStepConstants';

// The DraftSet fields the input area writes through. Mirrors the strength
// inputs + the cardio duration/optionals (parity with CardioSetRow).
export type EditableSetField =
  | 'reps'
  | 'weight'
  | 'extraNegative'
  | 'rir'
  | 'rpe'
  | 'duration'
  | 'distanceM'
  | 'avgHrBpm'
  | 'calories'
  | 'dropset';

// FocusPanelInputs (timeline logger) — the reps/weight (or cardio) data-entry
// area of the FocusPanel, extracted to keep FocusPanel under the 500-line
// budget. It owns the focus state for the contextual stepper bar and writes
// through `onChange` on every keystroke, exactly as it did inline. Rendered
// only when a set is bound; the dropset/myorep case defers its rounds to the
// DropsetBlock in FocusPanelDetails (the inline cells hide here, mirroring
// the card logger). Effort left this row in AP-0b: the meter under the card
// (FocusPanelEffort) is the single effort write path now.
export function FocusPanelInputs({
  set: s,
  isBodyweight,
  isCardio,
  isTimeBased,
  defaultUnit,
  prescribeLoad,
  onChange,
  onOpenPlates,
  onToggleUnit,
  onWeightFocus,
  theme,
}: {
  set: DraftSet;
  isBodyweight: boolean;
  isCardio: boolean;
  /** entry.isTimeBased — isometric hold; shows a duration field only. */
  isTimeBased: boolean;
  defaultUnit: WeightUnit;
  /** PE-34 — PRESCRIBE ONLY (undefined in the logger). Present means the
   *  weight field is authoring the PRESCRIBED load, not recording a lift:
   *  `kind` is the row's load axis and `cellUnit` the unit the draft's cells
   *  are expressed in. Two things follow, and both are the difference between
   *  a field and a trap:
   *   - the label follows the AXIS — "%" on percent-of-max, or the cell's own
   *     unit — because a field labelled kg holding 70 percent lies;
   *   - on an axis that carries NO number (history / sforzo / none) the field
   *     is not rendered at all, instead of accepting a value the kind ignores.
   *  The unit chip and the plate calculator go with it (PE-35): both act on a
   *  measured lift, and there is no lift here yet. */
  prescribeLoad?: {
    kind: IntensityKind;
    cellUnit: WeightUnit;
    /** The axis's own short label ("storico", "sforzo"…), resolved by the
     *  caller through useKindLabels — the ONE label resolver, which lives in
     *  ProgramComposer and must not be imported down into the Logger. Shown
     *  in place of the field on an axis that carries no number. */
    axisLabel: string;
  };
  onChange: (patch: Partial<Pick<DraftSet, EditableSetField>>) => void;
  onOpenPlates: () => void;
  onToggleUnit: () => void;
  /** Fired when the weight field gains focus — FocusPanel uses it for the
   *  once-per-exercise per-implement tip («enter ONE dumbbell's weight»).
   *  Kept as a callback so this component stays free of tip bookkeeping
   *  (useTransientTip lives once at FocusPanel level, via useHelpMode). */
  onWeightFocus?: () => void;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeFocusPanelInputsStyles(theme);

  // PE-34 — does this axis take a typed number at all? ⛔ The shared
  // predicate, never a fourth hand-written copy: the dispatch and the
  // hydration ask the same question and the three answers must agree.
  const loadAxisHasValue =
    prescribeLoad === undefined || isLoadBearingKind(prescribeLoad.kind);
  const weightUnitLabel =
    prescribeLoad?.kind === 'percent-of-max'
      ? t('mobile.logger.setPercentOfMax', { defaultValue: '%' })
      : (prescribeLoad?.cellUnit ?? defaultUnit) === 'lb'
        ? t('mobile.logger.setWeightLb', { defaultValue: 'LB' })
        : t('mobile.logger.setWeight');

  const [weightFocused, setWeightFocused] = useState(false);
  const [stepSign, setStepSign] = useState<1 | -1>(1);

  // Bodyweight extra: magnitude-only field, sign on extraNegative (same as
  // SetRow.onChangeWeight). A typed minus turns the field amber.
  const onChangeWeight = (text: string) => {
    if (!isBodyweight) {
      onChange({ weight: text });
      return;
    }
    const hasMinus = /[-−]/.test(text);
    const magnitude = text.replace(/[-−]/g, '').trim();
    if (hasMinus) onChange({ weight: magnitude, extraNegative: true });
    else if (magnitude === '') onChange({ weight: '', extraNegative: false });
    else onChange({ weight: magnitude });
  };
  const isAssist =
    isBodyweight && (s.extraNegative || /^\s*[-−]/.test(s.weight));
  const applyDelta = (delta: number) => {
    onChange({ weight: steppedWeight(s.weight, delta) });
  };

  // Tap a populated field → select the whole number so you type over it.
  const repsSel = useSelectAllOnFocus(s.reps);
  const weightSel = useSelectAllOnFocus(s.weight);
  // Tell the logger a field is holding the keyboard, so the rest-end resume
  // waits instead of pulling the caret out of a half-typed number (TM-18a).
  const fieldFocus = useFieldFocusReporter();

  // Inline numeric validation (2026-06-08), locale-aware; danger border on the
  // bad field + one message line below the input row.
  const repsErr = numericFieldErrorText('reps', s.reps, t);
  const weightErr = numericFieldErrorText('weight', s.weight, t);
  // A prescribed AMRAP starts with an empty reps cell (LG-28) and cannot be
  // completed until it is filled (useTimelineLogger.amrapNeedsReps). That rule
  // has to be READABLE, or the band tap just stops working for no visible
  // reason. It is not an error — nothing is wrong yet, the athlete simply has
  // not typed the result — so it renders as the row's cue and never turns the
  // field's border red. `validateNumericField` deliberately calls an empty
  // field valid (it must, or every field would flash mid-typing), so this
  // state cannot come from there and is computed here.
  const needsReps = amrapNeedsReps(s);
  const rowError = [repsErr, weightErr].filter(Boolean).join(' · ') || null;
  const rowCue =
    rowError === null && needsReps
      ? t('mobile.logger.amrapNeedsReps', {
          defaultValue: 'Type the reps you did to log this set.',
        })
      : null;

  return (
    <>
      <View style={styles.inputRow}>
        {isTimeBased ? (
          <FocusPanelTimed
            set={s}
            defaultUnit={defaultUnit}
            onChange={onChange}
            theme={theme}
          />
        ) : isCardio ? (
          <FocusPanelCardio
            set={s}
            onChange={onChange}
            theme={theme}
          />
        ) : s.dropset ? (
          // Dropset/myorep active: the chain rounds own input below (the
          // DropsetBlock in FocusPanelDetails). Hide the single reps/weight
          // row to avoid a second, conflicting parent input — mirrors the
          // card logger, where the type bar replaces the inline cells.
          <Text style={styles.hint}>
            {t('mobile.timeline.dropsetActive', {
              defaultValue: 'Dropset — edit the rounds below.',
            })}
          </Text>
        ) : (
          <>
            <View style={styles.field}>
              {/* A PRESCRIBED AMRAP used to live only in the placeholder, so it
                  vanished the moment the first digit was typed — exactly when
                  you still need to know the target is "at least N, then as many
                  as possible" (LG-23, inbox 3d88179b). The badge sits on the
                  label and STAYS. It is display-only on purpose: <Badge> is the
                  non-interactive status tag, never <Chip> — the AMRAP *toggle*
                  is a different control and stays hidden until the flag
                  persists ([[reference_amrap_via_reps_form]]). It reads
                  `amrapPrescribed` — the PLAN's request — never the
                  athlete's `amrap` outcome: the badge answers "what am I
                  being asked for?", the chip answers "what did I do?". */}
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>
                  {t('mobile.logger.setReps')}
                </Text>
                {s.amrapPrescribed ? (
                  <Badge
                    theme={theme}
                    // The floor rides on the badge, not only in the
                    // placeholder: a placeholder dies on the first keystroke,
                    // which is exactly when a two-digit answer needs checking
                    // against the target. The badge is permanent, so "AMRAP
                    // ≥ 8" is still readable while typing. Falls back to the
                    // bare word when there is no floor (a chip-marked AMRAP
                    // nobody prescribed).
                    label={
                      s.amrapFloor
                        ? t('mobile.logger.amrapBadgeWithFloor', {
                            defaultValue: 'AMRAP ≥ {{reps}}',
                            reps: s.amrapFloor,
                          })
                        : t('program.amrapBadge', { defaultValue: 'AMRAP' })
                    }
                    a11yLabel={
                      s.amrapFloor
                        ? t('mobile.logger.amrapPrescribedFloorA11y', {
                            defaultValue:
                              'AMRAP: at least {{reps}} reps, then as many as possible',
                            reps: s.amrapFloor,
                          })
                        : t('mobile.logger.amrapPrescribedA11y', {
                            defaultValue:
                              'AMRAP: at least the target reps, then as many as possible',
                          })
                    }
                  />
                ) : null}
              </View>
              <TextInput
                ref={repsSel.ref}
                style={[styles.input, repsErr && styles.inputInvalid]}
                keyboardType="number-pad"
                inputMode="numeric"
                // On a prescribed AMRAP the cell arrives EMPTY (LG-28) and the
                // floor lives here instead, as "≥ 8". A placeholder cannot be
                // submitted by doing nothing, which is the whole point: the
                // prefilled floor was being read as the answer and confirmed
                // as a limit. The badge above says AMRAP, this says how far in
                // the number has to start — together they read "at least 8,
                // then as many as you can". Every other set keeps the bare
                // dash: its cell is already seeded with the prescribed reps.
                placeholder={
                  s.amrapPrescribed && s.amrapFloor
                    ? t('mobile.logger.amrapFloorPlaceholder', {
                        defaultValue: '≥ {{reps}}',
                        reps: s.amrapFloor,
                      })
                    : '—'
                }
                placeholderTextColor={theme.palette.ink3}
                value={s.reps}
                onChangeText={(reps) => onChange({ reps })}
                selectTextOnFocus
                onFocus={() => {
                  repsSel.onFocus();
                  fieldFocus.onFocus('reps');
                }}
                onBlur={() => fieldFocus.onBlur('reps')}
                maxLength={4}
              />
            </View>
            {/* PE-34 — on history / sforzo / none the axis carries no number,
                so there is no field: an editable cell whose value the kind
                ignores is the swallowing input this slice removes.
                ⭐ The cell NAMES the axis instead of going blank — a control
                that vanishes with no explanation reads as a bug, and the
                selector that would explain it sits BELOW the row, so a
                top-down scan meets the effect before the cause (a11y audit).
                ⛔ Not a new idiom: ComposerSetRow's valueless branch already
                prints the kind label in the value cell. */}
            {!loadAxisHasValue ? (
              // ⭐ Banco visivo, 30/08: la prima forma metteva il nome dell'asse
              // all'altezza del VALORE e la colonna sembrava aver perso la sua
              // intestazione, mentre reps accanto ne aveva una. L'asse sta dove
              // sta l'etichetta, e sotto c'è il trattino che questa card usa già
              // per «qui non c'è un numero» — senza bordo, così non si legge
              // come un campo disabilitato.
              <View style={styles.field}>
                <Text style={styles.fieldLabel} numberOfLines={1}>
                  {prescribeLoad?.axisLabel ?? ''}
                </Text>
                <Text style={styles.axisOnlyText}>—</Text>
              </View>
            ) : (
            <View style={styles.field}>
              {/* Weight label carries the SAME tap-tip as the card logger's
                  weight column (HeaderTooltip, slice 5b) — explains kg/lb +
                  the ± assist convention for bodyweight rows. Reuses the
                  existing setWeightTip / setExtraTip keys; respects the Hints
                  toggle (off ⇒ plain label). Left-aligned to match the reps /
                  effort labels via the cellStyle override. */}
              {/* «2×» on the label when the set counts both sides (UNI /
                  two-implement): the convention — enter ONE implement's
                  weight — is readable right where the number is typed,
                  at zero layout cost (IB-8 family). Words-only a11yLabel:
                  the raw × glyph is never vocalised (audit 2026-07-14);
                  numberOfLines pins the row rhythm on wide locales (ФУНТ). */}
              <HeaderTooltip
                label={`${s.loadedSides === 2 ? '2× ' : ''}${
                  isBodyweight ? t('mobile.logger.setExtra') : weightUnitLabel
                }`}
                a11yLabel={
                  s.loadedSides === 2
                    ? t('mobile.logger.setWeightBothSidesA11y', {
                        unit: s.unit,
                        defaultValue:
                          'Weight of one side, both sides counted ({{unit}})',
                      })
                    : prescribeLoad?.kind === 'percent-of-max'
                      ? // "%" is a glyph, and a screen reader saying "percent"
                        // alone does not say percent OF WHAT. KG/LB read as
                        // words already; this one needed spelling out.
                        t('mobile.logger.setPercentOfMaxA11y', {
                          defaultValue: 'Percent of max',
                        })
                      : undefined
                }
                labelNumberOfLines={1}
                tip={
                  isBodyweight
                    ? t('mobile.logger.setExtraTip', {
                        defaultValue:
                          'Bodyweight assistance or added load. Type a minus (−) for band/machine assist (the field turns amber); a belt/plate add is positive.',
                      })
                    : prescribeLoad
                      ? t('mobile.logger.prescribedLoadTip', {
                          defaultValue:
                            "The load you're prescribing, in the unit the program is written in. Kilos or a % of max — that's set just below.",
                        })
                      : t('mobile.logger.setWeightTip', {
                          defaultValue:
                            'Weight lifted per set. Switch kg/lb with the unit chip on each row.',
                        })
                }
                labelStyle={styles.fieldLabel}
                cellStyle={styles.labelCell}
                theme={theme}
              />
              <TextInput
                ref={weightSel.ref}
                style={[
                  styles.input,
                  isAssist && styles.inputNegative,
                  weightErr && styles.inputInvalid,
                ]}
                keyboardType="decimal-pad"
                inputMode="decimal"
                placeholder="—"
                placeholderTextColor={theme.palette.ink3}
                value={s.weight}
                onChangeText={onChangeWeight}
                selectTextOnFocus
                onFocus={() => {
                  setWeightFocused(true);
                  weightSel.onFocus();
                  fieldFocus.onFocus('weight');
                  onWeightFocus?.();
                }}
                onBlur={() => {
                  setWeightFocused(false);
                  fieldFocus.onBlur('weight');
                }}
                maxLength={6}
              />
            </View>
            )}
            {/* Empty third cell: keeps reps + weight each a 2-unit cell of the
                6-unit card grid, aligned with the rows below. The effort value
                moved to the meter (FocusPanelEffort, AP-0b). */}
            <View style={styles.field} />
          </>
        )}
      </View>

      {rowError ? (
        <View style={styles.fieldError} accessibilityRole="alert">
          <Icon name="warn" size={theme.typography.sizes.xs} color={theme.palette.danger} />
          <Text style={styles.fieldErrorText}>{rowError}</Text>
        </View>
      ) : rowCue ? (
        // Same slot, different voice: no warn glyph and no danger tint, because
        // an unfilled AMRAP is the normal state of a set you have not done yet.
        // It carries a text sentence, so it never depends on colour.
        <View style={styles.fieldError}>
          <Text style={styles.fieldCueText}>{rowCue}</Text>
        </View>
      ) : null}

      {weightFocused && loadAxisHasValue && !isCardio && !isTimeBased && !s.dropset ? (
        <SetRowStepperBar
          unit={s.unit}
          onOpenPlates={
            isBodyweight || prescribeLoad ? undefined : onOpenPlates
          }
          onToggleUnit={prescribeLoad ? undefined : onToggleUnit}
          stepSign={stepSign}
          setStepSign={setStepSign}
          // A percentage does not step by plate sizes. 0.5/1.25/2.5 kg are the
          // increments a barbell can actually make; on %MAX the useful moves
          // are whole points (a11y audit, functional).
          stepMagnitudes={
            prescribeLoad?.kind === 'percent-of-max'
              ? STEPS_PERCENT
              : s.unit === 'lb'
                ? STEPS_LB
                : STEPS_KG
          }
          applyDelta={applyDelta}
          theme={theme}
        />
      ) : null}
    </>
  );
}
